import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { XMarkIcon } from "@heroicons/react/20/solid";
import { CaretDownIcon, CheckIcon } from "@radix-ui/react-icons";

import type { ProductRefinementType } from "~/lib/app.types";
import { cn } from "~/lib/ui";
import {
  clearRefinements,
  getRefinementsObjectFromString,
  updateRefinement,
} from "~/shop/utils/refinements";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";

interface FacetedRefinementProps {
  refinement: ProductRefinementType;
  selectedValues: string[];
  title?: string;
  openFilterId?: string | undefined;
  setFilterValue: (value: string[] | undefined) => void;
  onToggle?: () => void;
}
interface RefinementButtonProps {
  refinement: ProductRefinementType;
  selectedValues: string[];
  title?: string;
  onFilterClick: (value: string | undefined) => void;
  setFilterValue: (value: string[] | undefined) => void;
}
/**
 * This is the slot for the faceted refinements
 *
 */
export const FacetedRefinementsSlot = ({
  refinements,
  onFilterOpen,
}: {
  refinements?: ProductRefinementType[];
  onFilterOpen: (value: string | undefined) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRefinements = getRefinementsObjectFromString(
    searchParams.get("refine") ?? "",
  );
  const refinementsSlot = refinements
    ?.filter(ref => ref.attributeId !== "cgid")
    ?.filter(ref => ref.values?.length && ref.values?.length > 1)
    .slice(0, 4)
    .map(refinement => {
      return (
        <RefinementButton
          key={refinement.attributeId}
          title={refinement.label}
          refinement={refinement}
          selectedValues={selectedRefinements[refinement.attributeId] ?? []}
          onFilterClick={onFilterOpen}
          setFilterValue={values => {
            setSearchParams(
              prev => {
                prev.set(
                  "refine",
                  updateRefinement(
                    prev.get("refine") ?? "",
                    refinement.attributeId,
                    values ?? [],
                  ),
                );
                prev.delete("page");
                return prev;
              },
              {
                preventScrollReset: true,
              },
            );
          }}
        />
      );
    });

  return refinementsSlot;
};

export function RefinementButton({
  refinement,
  title,
  selectedValues,
  onFilterClick,
  setFilterValue,
}: RefinementButtonProps) {
  const options = useMemo(() => refinement.values ?? [], [refinement.values]);
  const availableSelectedOptions = useMemo(
    () =>
      options
        .filter(option => selectedValues.includes(option.value))
        .map(option => option.value),
    [options, selectedValues],
  );
  const selectedRefinementOptions = options.filter(option =>
    availableSelectedOptions.includes(option.value),
  );
  return (
    <Button
      variant="outline"
      size="inherit"
      className="h-8 min-w-fit flex-initial text-base font-semibold text-neutral-11"
      onClick={() => onFilterClick(refinement.attributeId)}
    >
      {title}
      {availableSelectedOptions?.length > 0 && (
        <>
          <Badge
            variant="secondary"
            className="mx-1.5 rounded-sm px-1.5 text-base font-semibold text-neutral-11 lg:hidden"
          >
            {availableSelectedOptions.length}
            <button
              type="button"
              className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-transparent text-neutral-9 hover:bg-gray-100"
              onClick={(e: { stopPropagation: () => void }) => {
                setFilterValue(undefined);
                e.stopPropagation();
              }}
            >
              <XMarkIcon />
            </button>
          </Badge>
          <div className="hidden space-x-1 lg:flex">
            {availableSelectedOptions.length > 2 ? (
              <Badge
                variant="secondary"
                className="mx-1.5 rounded-sm px-1 text-base font-semibold text-neutral-11"
              >
                {availableSelectedOptions.length} selected
                <button
                  type="button"
                  className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-transparent text-neutral-9 hover:bg-gray-100"
                  onClick={(e: { stopPropagation: () => void }) => {
                    setFilterValue(undefined);
                    e.stopPropagation();
                  }}
                >
                  <XMarkIcon />
                </button>
              </Badge>
            ) : (
              selectedRefinementOptions.map(option => (
                <Badge
                  variant="secondary"
                  key={option.value}
                  className="mx-1.5 rounded-sm px-1 text-base font-semibold text-neutral-11"
                >
                  {option.label}
                  <button
                    type="button"
                    className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-transparent text-neutral-9 hover:bg-gray-100"
                    onClick={(e: { stopPropagation: () => void }) => {
                      setFilterValue(
                        selectedRefinementOptions
                          .filter(o => o.value !== option.value)
                          .map(o => o.value),
                      );
                      e.stopPropagation();
                    }}
                  >
                    <XMarkIcon />
                  </button>
                </Badge>
              ))
            )}
          </div>
        </>
      )}
    </Button>
  );
}

export const FacetedRefinementsSlotContent = ({
  refinements,
  openFilterId,
  onToggle,
}: {
  refinements?: ProductRefinementType[];
  openFilterId?: string | undefined;
  onToggle?: () => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRefinements = getRefinementsObjectFromString(
    searchParams.get("refine") ?? "",
  );
  const refinementsSlot = refinements
    ?.filter(ref => ref.attributeId !== "cgid")
    ?.filter(ref => ref.values?.length && ref.values?.length > 1)
    .slice(0, 4)
    .map(refinement => {
      return (
        <FacetedRefinement
          key={refinement.attributeId}
          title={refinement.label}
          refinement={refinement}
          openFilterId={openFilterId}
          selectedValues={selectedRefinements[refinement.attributeId] ?? []}
          onToggle={onToggle}
          setFilterValue={values => {
            setSearchParams(
              prev => {
                prev.set(
                  "refine",
                  updateRefinement(
                    prev.get("refine") ?? "",
                    refinement.attributeId,
                    values ?? [],
                  ),
                );
                prev.delete("page");
                return prev;
              },
              {
                preventScrollReset: true,
              },
            );
          }}
        />
      );
    });
  return (
    <>
      {Object.values(selectedRefinements).flat().length > 0 && (
        <button
          onClick={() => {
            setSearchParams(
              prev => {
                prev.set("refine", clearRefinements(prev.get("refine") ?? ""));
                prev.delete("page");
                return prev;
              },
              {
                preventScrollReset: true,
              },
            );
            onToggle && onToggle();
          }}
          className="absolute right-[100px] top-[30px] my-2 flex items-center rounded bg-white px-2 py-2 text-base font-normal shadow hover:no-underline"
        >
          Clear all filters
        </button>
      )}
      {refinementsSlot}
    </>
  );
};

export function FacetedRefinement({
  refinement,
  title,
  selectedValues,
  openFilterId,
  setFilterValue,
  onToggle,
}: FacetedRefinementProps) {
  const options = useMemo(() => refinement.values ?? [], [refinement.values]);
  const availableSelectedOptions = useMemo(
    () =>
      options
        .filter(option => selectedValues.includes(option.value))
        .map(option => option.value),
    [options, selectedValues],
  );
  const [subMenuOpen, setSubMenuOpen] = useState<boolean>(
    openFilterId === refinement.attributeId,
  );
  return (
    <>
      <button
        onClick={() => {
          setSubMenuOpen(!subMenuOpen);
          onToggle && onToggle();
        }}
        className="relative my-2 flex w-full items-center rounded-full px-4 text-base text-lg font-normal text-neutral-11 hover:no-underline"
      >
        {title}
        {availableSelectedOptions?.length > 0 && (
          <>
            <Badge
              variant="secondary"
              className="mx-1.5 rounded-sm px-1.5 text-base font-semibold lg:hidden"
            >
              {availableSelectedOptions.length}
              <button
                type="button"
                className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-transparent text-neutral-9 hover:bg-gray-100"
                onClick={() => setFilterValue(undefined)}
              >
                <XMarkIcon />
              </button>
            </Badge>
            <div className="hidden space-x-1 lg:flex">
              {availableSelectedOptions.length > 1 ? (
                <Badge
                  variant="secondary"
                  className="mx-1.5 rounded-sm px-1 text-base font-semibold text-neutral-11"
                >
                  {availableSelectedOptions.length} selected
                  <button
                    type="button"
                    className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-transparent text-neutral-9 hover:bg-gray-100"
                    onClick={() => setFilterValue(undefined)}
                  >
                    <XMarkIcon />
                  </button>
                </Badge>
              ) : (
                options
                  .filter(option =>
                    availableSelectedOptions.includes(option.value),
                  )
                  .map(option => (
                    <Badge
                      variant="secondary"
                      key={option.value}
                      className="mx-1.5 rounded-sm px-1 text-base font-semibold text-neutral-11"
                    >
                      {option.label}
                      <button
                        type="button"
                        className="inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-transparent text-neutral-9 hover:bg-gray-100"
                        onClick={() => setFilterValue(undefined)}
                      >
                        <XMarkIcon />
                      </button>
                    </Badge>
                  ))
              )}
            </div>
          </>
        )}
        <CaretDownIcon
          className={cn(
            "absolute right-4 ml-1 inline h-5 w-5 text-neutral-11 transition-all",
            subMenuOpen && "rotate-180",
          )}
        />
      </button>
      <div className={cn("w-full p-0", !subMenuOpen && "hidden")}>
        <Command className="bg-transparent">
          <CommandList className="max-h-none">
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map(option => {
                const isSelected = availableSelectedOptions.includes(
                  option.value,
                );
                return (
                  <CommandItem
                    key={option.value}
                    className="px-4 py-3"
                    onSelect={() => {
                      const selectedValues = new Set(availableSelectedOptions);
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      setFilterValue(
                        filterValues.length ? filterValues : undefined,
                      );
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-neutralalpha-7",
                        isSelected
                          ? "bg-accent-9 text-white"
                          : "bg-neutral-2 [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>
                    <span className="text-dark-on-light text-base font-normal">
                      {option.label}
                    </span>
                    {option.hitCount && (
                      <span className="text-dark-on-light ml-auto flex h-4 w-4 items-center justify-center text-base font-normal">
                        {option.hitCount}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {availableSelectedOptions.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => setFilterValue(undefined)}
                    className="justify-start text-left"
                  >
                    <span className="text-base text-sm font-semibold text-neutral-11">
                      Clear filters
                    </span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </div>
    </>
  );
}
